import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, X, LogOut, Users } from "lucide-react";
import { searchIssues } from "@/lib/api";
import { ISSUE_TYPE_CONFIG } from "@/lib/constants";
import { useAuth } from "@/lib/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Topbar({ currentUser, onCreateIssue }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const results = await searchIssues(searchQuery);
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearch(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleResultClick = (issue) => {
    setShowSearch(false);
    setSearchQuery("");
    const boardMap = {
      INITIATIVE: "initiatives",
      DESIGN: "design",
      FRONTEND: "frontend",
      BACKEND: "backend",
      BUG: "bugs",
      QA: "backend",
    };
    const board = boardMap[issue.type] || "backend";
    navigate(`/project/${issue.project_key}/${board}?issue=${issue.issue_key}`);
  };

  return (
    <div
      className="h-14 border-b border-gray-200 bg-white px-6 flex items-center justify-between flex-shrink-0"
      data-testid="topbar"
    >
      {/* Search */}
      <div className="relative flex-1 max-w-md" ref={searchRef}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            data-testid="global-search-input"
            type="text"
            placeholder="Search issues..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSearch(true);
            }}
            onFocus={() => setShowSearch(true)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-sm bg-gray-50 focus:bg-white focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                setSearchResults([]);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2"
            >
              <X className="w-3.5 h-3.5 text-gray-400 hover:text-black" />
            </button>
          )}
        </div>
        {showSearch && searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-lg z-50 max-h-80 overflow-y-auto">
            {searchResults.map((issue) => {
              const typeConf = ISSUE_TYPE_CONFIG[issue.type] || {};
              return (
                <button
                  key={issue.id}
                  data-testid={`search-result-${issue.issue_key}`}
                  onClick={() => handleResultClick(issue)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left border-b border-gray-100 last:border-0"
                >
                  <span
                    className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm"
                    style={{ backgroundColor: typeConf.bg, color: typeConf.text }}
                  >
                    {typeConf.label}
                  </span>
                  <span className="text-xs font-mono text-gray-500">{issue.issue_key}</span>
                  <span className="text-sm text-gray-900 truncate flex-1">{issue.title}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-3 ml-4">
        <button
          data-testid="create-issue-button"
          onClick={onCreateIssue}
          className="bg-brand text-white hover:bg-brand-dark rounded-sm px-4 py-2 text-sm font-semibold transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create
        </button>

        {currentUser && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-profile-dropdown" className="outline-none">
                <Avatar className="h-8 w-8 border border-gray-200">
                  <AvatarImage src={currentUser.avatar_url} alt={currentUser.full_name} />
                  <AvatarFallback className="text-xs font-semibold">
                    {currentUser.full_name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-sm">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-semibold">{currentUser.full_name}</p>
                  <p className="text-xs text-gray-500">{currentUser.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="dropdown-profile-link"
                className="text-sm rounded-sm cursor-pointer"
                onClick={() => navigate("/profile")}
              >
                Profile
              </DropdownMenuItem>
              {currentUser.role === "ADMIN" && (
                <>
                  <DropdownMenuItem
                    data-testid="dropdown-users-link"
                    className="text-sm rounded-sm cursor-pointer"
                    onClick={() => navigate("/admin/users")}
                  >
                    <Users className="w-3.5 h-3.5 mr-2" />
                    Users
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="dropdown-settings-link"
                    className="text-sm rounded-sm cursor-pointer"
                    onClick={() => navigate("/admin/slack")}
                  >
                    Settings
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="dropdown-logout"
                className="text-sm rounded-sm cursor-pointer text-red-600 focus:text-red-600"
                onClick={() => logout()}
              >
                <LogOut className="w-3.5 h-3.5 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
